
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, ArrowLeft, Save } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/sonner";

const UserManagementSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    defaultRole: 'user',
    allowSelfRegistration: true,
    requireEmailVerification: true,
    autoApproveRegistrations: false,
    maxUsersPerAccount: 1000,
    sessionDuration: 30,
    allowMultipleSessions: true,
    requireProfileCompletion: false
  });

  const handleSave = () => {
    toast.success("User management settings saved successfully");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')}>
          <ArrowLeft className="h-4 w-4" />
          Back to System Settings
        </Button>
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">User Management Settings</h1>
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Registration Settings</CardTitle>
            <CardDescription>Configure how users can register and join the platform</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Allow Self Registration</Label>
                <p className="text-sm text-muted-foreground">Allow users to register without admin approval</p>
              </div>
              <Switch
                checked={settings.allowSelfRegistration}
                onCheckedChange={(checked) => setSettings({...settings, allowSelfRegistration: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Require Email Verification</Label>
                <p className="text-sm text-muted-foreground">Users must verify their email before accessing the platform</p>
              </div>
              <Switch
                checked={settings.requireEmailVerification}
                onCheckedChange={(checked) => setSettings({...settings, requireEmailVerification: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Auto-approve Registrations</Label>
                <p className="text-sm text-muted-foreground">Automatically approve new user registrations</p>
              </div>
              <Switch
                checked={settings.autoApproveRegistrations}
                onCheckedChange={(checked) => setSettings({...settings, autoApproveRegistrations: checked})}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Default User Settings</CardTitle>
            <CardDescription>Configure default settings for new users</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="defaultRole">Default User Role</Label>
              <Select value={settings.defaultRole} onValueChange={(value) => setSettings({...settings, defaultRole: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="instructor">Instructor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Require Profile Completion</Label>
                <p className="text-sm text-muted-foreground">Force users to complete their profile before accessing content</p>
              </div>
              <Switch
                checked={settings.requireProfileCompletion}
                onCheckedChange={(checked) => setSettings({...settings, requireProfileCompletion: checked})}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Access Controls</CardTitle>
            <CardDescription>Configure user limits and session management</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="maxUsers">Maximum Users Per Account</Label>
                <Input
                  id="maxUsers"
                  type="number"
                  value={settings.maxUsersPerAccount}
                  onChange={(e) => setSettings({...settings, maxUsersPerAccount: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label htmlFor="sessionDuration">Session Duration (days)</Label>
                <Input
                  id="sessionDuration"
                  type="number"
                  value={settings.sessionDuration}
                  onChange={(e) => setSettings({...settings, sessionDuration: parseInt(e.target.value)})}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Allow Multiple Sessions</Label>
              <Switch
                checked={settings.allowMultipleSessions}
                onCheckedChange={(checked) => setSettings({...settings, allowMultipleSessions: checked})}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserManagementSettings;
